import { debug, Debugger } from 'debug';
import {
    getSandboxEnvironment,
    root,
    ISandboxEnvironment,
    getServiceVersion,
    AnyObject
} from '@estee/elc-universal-utils';
import * as Sentry from '@sentry/browser';

export interface IFullData {
    logdata: ILogData;
    level: string;
    config: IELCLogger;
}

export interface IPayload {
    error?: Error;
    context?: object;
    serviceRegistry?: object;
    serviceName?: string;
    serviceVersion?: string;
    siteUrl?: string;
}

export interface ILogData {
    triggerType: TriggerType;
    message: string | {
        error: Error
    } | Error;
    payload?: IPayload;
}

export enum TriggerType {
    runtimeError = 'runtimeError',
    api = 'api',
    graphqlApi = 'graphqlApi',
    render = 'render',
    serviceBus = 'serviceBus',
    config = 'config',
    translation = 'translation',
    other = 'other'
}

export interface IELCLogger {
    serviceName: string;
    serviceVersion?: string;
    environment?: string;
    buid?: string;
}

type ServicesLog = {
    [key: string]: {
        version: string;
        isStarted: boolean | null;
    }
};

class CustomError extends Error {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public cause: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    constructor(message: string, options: any) {
        // @ts-ignore
        super(message, options);

        if ("captureStackTrace" in Error) {
            Error.captureStackTrace(this);
        }

        if (options.cause) {
            this.cause = options.cause;
        }
    }
}

export class ELCLogger {
    private config: IELCLogger;
    private errorDebugger?: Debugger;
    private infoDebugger?: Debugger;
    private warningDebugger?: Debugger;
    private eventDebugger?: Debugger;
    private queryDebugger?: Debugger;
    private commandDebugger?: Debugger;

    constructor(config: IELCLogger) {
        this.config = config;
        const logger = debug(this.config.serviceName);

        this.errorDebugger = logger.extend('error');
        this.infoDebugger = logger.extend('info');
        this.warningDebugger = logger.extend('warning');
        this.eventDebugger = logger.extend('event');
        this.queryDebugger = logger.extend('query');
        this.commandDebugger = logger.extend('command');
    }

    public error = async (logdata: ILogData) => {
        if (this.errorDebugger) {
            this.errorDebugger(this.enhanceLogData(logdata));
        }

        const isProd =
            this.config.environment === ISandboxEnvironment.PRODUCTION ||
            root.env === ISandboxEnvironment.PRODUCTION;

        if ((isProd && getSandboxEnvironment() === ISandboxEnvironment.LIVE) || !isProd) {
            return this.sendLog(logdata, 'error');
        }
    };

    public info = (logdata: ILogData) => {
        if (this.infoDebugger) {
            this.infoDebugger(this.enhanceLogData(logdata));
        }
    };

    public warning = (logdata: ILogData) => {
        if (this.warningDebugger) {
            this.warningDebugger(this.enhanceLogData(logdata));
        }
    };

    public event = (logdata: ILogData) => {
        if (this.eventDebugger) {
            this.eventDebugger(this.enhanceLogData(logdata));
        }
    };

    public query = (logdata: ILogData) => {
        if (this.queryDebugger) {
            this.queryDebugger(this.enhanceLogData(logdata));
        }
    };

    public command = (logdata: ILogData) => {
        if (this.commandDebugger) {
            this.commandDebugger(this.enhanceLogData(logdata));
        }
    };

    private enhanceLogData = (logdata: ILogData) => ({
        ...logdata,
        triggerSource: this.config.serviceName,
        date: new Date().toUTCString()
    });

    private getFSUrl = () => {
        try {
            // @ts-ignore
            const fsUrl = root.FS.getCurrentSessionURL();

            return fsUrl || '';
        } catch (e) {
            return '';
        }
    };

    private sendToSentry = (data: CustomError, contextMsg = '') => {
        const isSentryAvailable = root.__SENTRY__ || root.Sentry;

        if (isSentryAvailable) {
            const fsUrl = this.getFSUrl();

            const serviceRegistryKeys = Object.keys(root.ServiceRegistry || {}).length;
            const serviceInfoKeys = Object.keys(root.serviceInfo || {}).length;
            const services: ServicesLog = {};

            if (serviceRegistryKeys <= serviceInfoKeys) {
                Object.entries(root.serviceInfo || {}).forEach(([key, value]) => {
                    const registry = root.ServiceRegistry || {};
                    const serviceRegistry = registry[key] || value;

                    services[key] = {
                        isStarted: serviceRegistry?.isStarted || null,
                        version: value?.version || getServiceVersion(key, 'x.x.x')
                    };
                });
            } else {
                Object.entries(root.ServiceRegistry || {}).forEach(([key, value]) => {
                    services[key] = {
                        isStarted: value?.isStarted,
                        version: value?.version
                    };
                });
            }

            Sentry.setContext('sd-services', {
                'fs-url': fsUrl,
                ...services,
                cause: JSON.stringify(data.cause),
                message: contextMsg
            });

            if (fsUrl) {
                this.info({
                    message: 'Setting FullStory URL to Sentry',
                    triggerType: TriggerType.other,
                    payload: {
                        context: {
                            url: fsUrl
                        }
                    }
                });
                Sentry.setTag('FS', 'true');
            }
            Sentry.captureException(data);
        } else {
            console.warn('Logging failed because Sentry is not available');
            console.warn(
                JSON.stringify(data, ["message", "arguments", "type", "name"])
            );
        }
    };

    private getError = (logdata: ILogData, context: AnyObject) => {
        const { payload = {}, message } = logdata;
        const msg = (
            typeof message === 'string' ? message : JSON.stringify(message)
        ) || 'Unknown error';
        let error: CustomError;

        if (message instanceof Error) {
            error = message as CustomError;
        } else if (typeof message !== 'string' && message.error instanceof Error) {
            error = message.error as CustomError;
        } else if (payload.error) {
            error = new CustomError(msg, { cause: payload.error });
        } else if (typeof message !== 'string') {
            error = new CustomError('Unknown error', { cause: message });
        } else {
            error = new CustomError(msg, { cause: context });
        }

        return { error, message: msg };
    };

    private getPayload = (payload: AnyObject): AnyObject => {
        const { error, siteUrl, serviceRegistry: _, context = {}, ...restPayload } = payload;

        // @ts-ignore
        const { serviceRegistry: __, ...restContext } = context;

        return {
            ...restPayload,
            ...restContext
        };
    };

    // @ts-ignore
    private async sendLog(logdata: ILogData, level: string) {
        try {
            const { triggerType, payload = {}, message } = logdata;

            if (
                (typeof message === 'string' && message.includes('Script error'))
                // @ts-ignore
                || message?.message?.includes('Script error')) {
                return ;
            }

            if (message instanceof Event) {
                if (Object.keys(JSON.parse(JSON.stringify(message))).length <= 1) {
                    // This is a workaround for the case when the message is an event and the event has no data
                    return ;
                }
            }

            const payloadData = this.getPayload({ ...payload, triggerType });
            Error.stackTraceLimit = 20;
            const { error, message: msg } = this.getError(logdata, payloadData);

            this.sendToSentry(error, msg);
        } catch (e) {
            if (this.errorDebugger) {
                this.errorDebugger(
                    this.enhanceLogData({
                        message: 'Could not send log',
                        triggerType: TriggerType.api,
                        payload: {
                            error: e,
                            context: logdata
                        }
                    })
                );
            }
        }
    }
}
